import { ref, onMounted, computed } from 'vue';
import axios from 'axios';

export const isLocked = !formychat_vars.is_premium

export const countries = formychat_vars.data.countries.sort((a, b) => a.name.localeCompare(b.name)).map(country => {
    return {
        value: country.code,
        label: `<span>${country.flag} (${country.code})</span> ${country.name}`,
        name: `<span>${country.flag}</span> ${country.code}`
    }
})

export const headingSizes = { small: '16px', medium: '18px', large: '20px' }
export const textSizes = { small: '12px', medium: '14px', large: '16px' }
export const iconSizes = { small: '45px', medium: '60px', large: '80px' }

export const getSize = (size, size_custom, options = textSizes) => {
    return options[size] || (isLocked ? options.medium : (size_custom + 'px'));
}

export const isDesktop = () => {
   // Check if it's a mobile device. Do not check screen size because this can be inside a iframe too.
    return !navigator.userAgent.match(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i);
}

/**
 * Mirrors the admin store helper — treats false / 0 / "0" / "false" as off.
 * Used by every third-party integration to honour the "Send message to WhatsApp" toggle.
 * @param {object} whatsappConfig - widget.config.whatsapp
 * @returns {boolean}
 */
export const isSendMessageToWhatsappEnabled = (whatsappConfig) => {
    const v = whatsappConfig?.send_message_to_whatsapp;
    if (v === false || v === 0 || v === '0' || v === 'false') return false;
    return true;
}

export const isSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

/**
 * Extract group invite code from URL or raw string.
 * @param {string} input - Full URL (https://chat.whatsapp.com/XXX) or invite code.
 * @returns {string|null} Extracted invite code or null.
 */
const extractGroupCode = (input) => {
    if (!input || typeof input !== 'string') return null;
    const trimmed = input.trim();
    if (!trimmed) return null;
    const match = trimmed.match(/chat\.whatsapp\.com\/([A-Za-z0-9_-]+)/);
    if (match) return match[1];
    // Only allow alphanumeric, underscore, hyphen for raw code
    return /^[A-Za-z0-9_-]+$/.test(trimmed) ? trimmed : null;
};

/**
 * Show popup for group message: copy confirmation + paste instructions.
 * On OK click: close popup and open the WhatsApp group URL.
 * @param {string} url - WhatsApp group URL to open on OK.
 * @param {boolean} newTab - Open in new tab.
 * @param {string|number} [widgetId] - Widget ID for parent to close widget after OK.
 */
const showGroupCopyPopup = (url, newTab, widgetId) => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const openUrl = () => {
        overlay.remove();
        if (newTab && !isSafari) {
            try {
                const w = window.open('about:blank', '_blank');
                if (w) {
                    w.opener = null;
                    w.location.href = url;
                } else {
                    window.open(url, '_top');
                }
            } catch (e) {
                window.open(url, '_top');
            }
        } else {
            window.open(url, '_top');
        }
    };
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'formychat-popup-title');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:999999;display:flex;align-items:center;justify-content:center;padding:20px;font-family:system-ui,sans-serif;';
    overlay.innerHTML = `
        <div style="background:#fff;border-radius:12px;max-width:400px;width:100%;box-shadow:0 4px 24px rgba(0,0,0,0.15);overflow:hidden;">
            <div style="padding:24px 24px 16px;">
                <h2 id="formychat-popup-title" style="margin:0 0 12px;font-size:18px;font-weight:600;color:#1a1a1a;">Message copied!</h2>
                <p style="margin:0 0 16px;font-size:14px;line-height:1.5;color:#4a4a4a;">Your message has been copied to the clipboard. Please paste it manually into the WhatsApp group chat.</p>
                <div style="background:#f5f5f5;border-radius:8px;padding:16px;font-size:13px;line-height:1.6;color:#333;">
                    <p style="margin:0 0 8px;font-weight:600;">Desktop:</p>
                    <p style="margin:0 0 12px;">Press <kbd style="background:#fff;padding:2px 6px;border-radius:4px;border:1px solid #ddd;">Ctrl+V</kbd> (Windows) or <kbd style="background:#fff;padding:2px 6px;border-radius:4px;border:1px solid #ddd;">Cmd+V</kbd> (Mac) in the message input field.</p>
                    <p style="margin:0 0 8px;font-weight:600;">Mobile:</p>
                    <p style="margin:0;">Long-press in the message input field and select "Paste".</p>
                </div>
            </div>
            <div style="padding:16px 24px 24px;text-align:right;">
                <button type="button" style="background:#25D366;color:#fff;border:none;padding:10px 24px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;">Go to WhatsApp</button>
            </div>
        </div>
    `;
    const dismissWithoutNavigate = () => {
        document.removeEventListener('keydown', handleKeydown);
        overlay.remove();
    };
    const handleKeydown = (e) => {
        if (e.key === 'Escape') {
            dismissWithoutNavigate();
        }
    };
    overlay.querySelector('button').addEventListener('click', () => {
        document.removeEventListener('keydown', handleKeydown);
        openUrl();
    });
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            dismissWithoutNavigate();
        }
    });
    document.addEventListener('keydown', handleKeydown);
    document.body.appendChild(overlay);
    overlay.querySelector('button').focus();
};

export const openWhatsapp = (to, text = '', isWeb = false, newTab = false, config = null) => {

    // Group destination: wa.me does NOT work for groups (causes 404). Use chat.whatsapp.com.
    if (config?.destination_type === 'group') {
        const code = extractGroupCode(config?.group_invite_code || to);
        if (code) {
            if (typeof window.formychat_open_whatsapp_group_handler === 'function') {
                return window.formychat_open_whatsapp_group_handler(to, text, isWeb, newTab, config);
            }
            // Fallback: copy message + show popup (user clicks OK to open group)
            const message = (text || '');
            const url = 'https://chat.whatsapp.com/' + code;
            const widgetId = config && (config.widget_id !== undefined && config.widget_id !== null) ? config.widget_id : null;
            const targetOrigin = (typeof formychat_vars !== 'undefined' && formychat_vars?.site?.url)
                ? new URL(formychat_vars.site.url).origin
                : '*';
            const showPopup = () => {
                // When in iframe (widget), show popup in parent so it's not covered by the floating box.
                if (widgetId && window.parent !== window) {
                    try {
                        window.parent.postMessage({ formychat_show_group_popup: { url, newTab, widgetId } }, targetOrigin);
                    } catch (e) {
                        showGroupCopyPopup(url, newTab, widgetId);
                    }
                } else {
                    showGroupCopyPopup(url, newTab, widgetId);
                }
            };
            if (message && navigator.clipboard) {
                navigator.clipboard.writeText(message).then(showPopup).catch(showPopup);
            } else {
                showPopup();
            }
            return;
        }
    }

    // Text may have multiple line.
    text = text.replace(/\n/g, "\n");
    
    let url = `https://wa.me/${to}?text=${encodeURIComponent(text)}`;

    // For web version.
    if (isWeb && isDesktop() && !isLocked) url = `https://web.whatsapp.com/send?phone=${to}&text=${encodeURIComponent(text)}`;
    
    if (newTab && !isSafari()) {
        try {
            // Create the new window/tab immediately
            const newWindow = window.open('about:blank', '_blank');
            
            // If popup was blocked, fallback to _top
            if (newWindow === null) {
                window.open(url, '_top');
                return;
            }

            // Set proper window features for security
            if (newWindow) {
                newWindow.opener = null;
                newWindow.location.href = url;
            }
        } catch (e) {
            // Fallback to _top if any error occurs
            window.open(url, '_top');
        }
    } else {
        window.open(url, '_top');
    }
}

/**
 * Save lead to the database.
 */

export const save = async (data, whatsapp = null, message = null, isDesktop = null, newTab = null, recaptchaToken = null, turnstileToken = null) => {

    // If whatsapp, message, isDesktop, newTab provided. Add them to data.meta
    if (whatsapp) {
        data.meta.whatsapp = whatsapp
        data.meta.message = message
        data.meta.isDesktop = isDesktop
        data.meta.newTab = newTab
    }

    // Attach spam-protection tokens when provided.
    if (recaptchaToken) {
        data.recaptcha_token = recaptchaToken
    }
    if (turnstileToken) {
        data.turnstile_token = turnstileToken
    }

    if ( ! formychat_vars.is_form_page ) {
        // Trigger custom event.
        const event = new CustomEvent('formychat_form_submitted', {
            detail: {
                ...data,
                isWidget: formychat_vars.is_form_page || false
            }
        });
        const dispatched = document.dispatchEvent(event);
    }

    // console.log('Saving lead', data);
    return new Promise(async (resolve) => {
        axios.post(formychat_vars.rest_url + '/submit-form', data, {
            headers: {
                'X-WP-Nonce': formychat_vars.rest_nonce,
            }
        }).then(response => {
            // console.log('Lead saved', response.data);
            resolve(response.data)
        })
            .catch(error => {
                // console.error('Error saving lead', error);
                resolve(error)
            })
    })
}


/**
 * Meta data.
 */
export const meta = ref({
    Device: isDesktop() ? 'Desktop' : 'Mobile',
    Browser: navigator.appCodeName || 'Unknown Browser',
    Platform: navigator.platform || 'Unknown Platform',
    OS: navigator.oscpu || 'Unknown OS',
    Screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    Language: navigator.language || 'en',
    Vendor: navigator.vendor || 'Unknown Vendor',
    URL: window.parent.location.href || window.location.href || 'Unknown URL',
    Referrer: document.referrer || 'Direct',
})

export const getMeta = computed(() => {

    // Add IP data if available.
    if (window.formychat_ip) {
        meta.value = Object.assign({}, meta.value, {
            Country_Code: window.formychat_ip.country_code || 'US',
            Country: (window.formychat_ip.country + ` <img src="${window.formychat_ip.country_flag}" />`) || 'Unknown Country',
            City: window.formychat_ip.city || 'Unknown City',
            Region: window.formychat_ip.region || 'Unknown Region',
            Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            Timezone_GMT: window.formychat_ip.timezone_gmt || '+00:00',
            Currency: window.formychat_ip.currency || 'USD',
            IP: window.formychat_ip.ip || '0.0.0.0',
            Latitude: window.formychat_ip.latitude || 0,
            Longitude: window.formychat_ip.longitude || 0,
            ISP: window.formychat_ip.isp || 'Unknown ISP',
            Organization: window.formychat_ip.org || 'Unknown Organization',
        })
    }

    // Add user data if available.
    if ( formychat_vars.user && formychat_vars.user.id ) {
        meta.value = Object.assign({}, meta.value, {
            User_ID: formychat_vars.user.id || 0,
            User_First_Name: formychat_vars.user.first_name || '',
            User_Last_Name: formychat_vars.user.last_name || '',
            User_Email: formychat_vars.user.email || '',
            User_Phone: formychat_vars.user.phone || '',
        })
    }

    return meta.value
})

export const getGravityForm = (html) => {
    html = new DOMParser().parseFromString(html, 'text/html')

    let form = html.querySelector('form')

    if (form) {

        // change action
        form.setAttribute('action', 'javascript:void(0)')

        // Remove onClick from submit button.
        let submitButton = html.querySelector('input[type="submit"]')

        if (submitButton) {

            submitButton.removeAttribute('onClick')
            submitButton.removeAttribute('onKeyPress')
        }
    }

    return html.body
}


export const getData = ( data = {} ) => {
    // Merge data with formychat_vars.custom_tags
    return Object.assign({}, data, formychat_vars.custom_tags, {
        page_url: window.parent.location.href || window.location.href,
    })
}

export const titleCase = (text) => {
    // Replace underscores, hyphens with spaces and capitalize each word.
    return text.replace(/_/g, ' ').replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

export const getMessage = ( text, data = {}, pattern = /{(.*?)}/ ) => {

    // If text is blank, build test from data.
    if (!text) {
        // Each data key: {value}
        text = Object.entries(data).map(([key, value]) => `${titleCase(key)}: {${key}}`).join('\n');
        // console.log('text', text);
    }

    data = getData(data) 
    
    while (pattern.test(text)) {
        text = text.replace(pattern, (match, group) => {
            return data[group] || '';
        });
    }

    return text
}